import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/toaster';
import { TooltipProvider } from './components/ui/tooltip';
import Shipment from './Shipment';
import USAHelper from './USAHelper/USAHelper';
import FoodFDACheck from './USAHelper/FoodFDACheck';
import PurchaseLabels from './USAHelper/PurchaseLabels';
import ValidateShipmentInfo from './USAHelper/ValidateShipmentInfo';
import { USAProvider } from './USAHelper/USAContext';
import TestPrinterDropdown from './TestPrinterDropdown';
import StallionStatus from './components/StallionStatus';
import StallionReconcile from './components/StallionReconcile';
import StallionCheck from './components/StallionCheck';
import Stallion from './components/Stallion';

// Importing new billing tool components
import ClientOverview from './BillingTool/ClientOverview';
import ClientDetail from './BillingTool/ClientDetail';
import BillingReport from './BillingTool/BillingReport';
// Importing new inbound tool components
import InboundDetail from './InboundTool/InboundDetail';
import InboundSearch from './InboundTool/InboundSearch';
import ShelfSearch from './InboundTool/ShelfSearch';
import AddToShelf from './InboundTool/AddToShelf';
import DeliveryLog from './InboundTool/DeliveryLog';
import ClientShelves from './InboundTool/ClientShelves';
import InboundHistory from './InboundTool/InboundHistory';
import ASINCountryDatabase from './InboundTool/ASINCountryDatabase';
import ReceivingScanner from './InboundTool/ReceivingScanner';
import DeliveryReceiving from './InboundTool/DeliveryReceiving';
import PDAScanner from './InboundTool/PDAScanner';
import InboundSidebar from './InboundTool/InboundSidebar';
import { InboundProvider } from './InboundTool/InboundContext';  // Import the InboundProvider
// import { VoiceProvider } from './InboundTool/voice/VoiceContext';  // Voice AI Provider - DISABLED
// Importing BillingContext
import OutboundSearch from './OutboundSearch';
import OutboundFDA from './OutboundFDA';
import { BillingProvider } from './BillingTool/BillingContext';
import Tasks from './Tasks/Tasks';
import Financials from './routes/Financials/Financials';
import TestRadix from './test-radix';
import PatternAnalysis from './Pattern/PatternAnalysis';

// FBA Inbound v2024 imports
import FBAInboundWizard from './FBAInbound/FBAInboundWizard';
import FBAInboundSidebar from './FBAInbound/FBAInboundSidebar';
import { FBAInboundProvider } from './FBAInbound/FBAInboundContext';

// Analytics Engine imports
import AnalyticsLayout from './routes/Analytics/AnalyticsLayout';
import { AnalyticsProvider } from './routes/Analytics/AnalyticsContext';
import PinProtection from './routes/Analytics/PinProtection';

function App() {
  return (
    <TooltipProvider>
      <Router>
          <BillingProvider>
            <Routes>
          <Route path="/" element={<Navigate to="/inbounds" replace />} />
          
          {/* Test Route */}
          <Route path="/test-radix" element={<TestRadix />} />
          
          {/* Outbound Routes - More specific route first */}
          <Route path="/outbound/search" element={<OutboundSearch />} />
          <Route path="/outbound/:id/fda" element={<OutboundFDA />} />
          <Route path="/outbound/:shipmentId" element={<Shipment />} />
          
          {/* USA Routes */}
          <Route path="/usa/:shipmentId" element={
            <USAProvider>
              <USAHelper />
            </USAProvider>
          } />
          <Route path="/usa/:shipmentId/fda" element={
            <USAProvider>
              <FoodFDACheck />
            </USAProvider>
          } />
          <Route path="/usa/:shipmentId/validateAddress" element={
            <USAProvider>
              <ValidateShipmentInfo />
            </USAProvider>
          } />
          <Route path="/usa/:shipmentId/purchaseLabels" element={
            <USAProvider>
              <PurchaseLabels />
            </USAProvider>
          } />

          {/* New Billing Tool Routes */}
          <Route path="/billing" element={<ClientOverview />} />
          <Route path="/billing/client/:teamId/:startDate/:endDate" element={<ClientDetail />} />
          <Route path="/billing/report" element={<BillingReport />} />

         {/* New Inbound Tool Routes */}
         <Route path="/inbounds" element={
              <InboundProvider>
                <InboundSidebar>
                  <InboundSearch />
                </InboundSidebar>
              </InboundProvider>
          } />
          <Route path="/inbounds/:id" element={
              <InboundProvider>
                <InboundSidebar>
                  <InboundDetail />
                </InboundSidebar>
              </InboundProvider>
          } />
          <Route path="/inbounds/search" element={
              <InboundProvider>
                <InboundSidebar>
                  <InboundSearch />
                </InboundSidebar>
              </InboundProvider>
          } />
          <Route path="/inbounds/receiving-scanner" element={
              <InboundProvider>
                <InboundSidebar>
                  <ReceivingScanner />
                </InboundSidebar>
              </InboundProvider>
          } />
          <Route path="/inbounds/shelf-search" element={
              <InboundProvider>
                <InboundSidebar>
                  <ShelfSearch />
                </InboundSidebar>
              </InboundProvider>
          } />
          <Route path="/inbounds/add-to-shelf" element={
              <InboundProvider>
                <InboundSidebar>
                  <AddToShelf />
                </InboundSidebar>
              </InboundProvider>
          } />
          <Route path="/inbounds/delivery-log" element={
              <InboundProvider>
                <InboundSidebar>
                  <DeliveryLog />
                </InboundSidebar>
              </InboundProvider>
          } />
          <Route path="/inbounds/client-shelves" element={
              <InboundProvider>
                <InboundSidebar>
                  <ClientShelves />
                </InboundSidebar>
              </InboundProvider>
          } />
          <Route path="/inbounds/history" element={
              <InboundProvider>
                <InboundSidebar>
                  <InboundHistory />
                </InboundSidebar>
              </InboundProvider>
          } />
          <Route path="/inbounds/asin-country-database" element={
              <InboundProvider>
              <InboundSidebar>
                <ASINCountryDatabase />
              </InboundSidebar>
            </InboundProvider>
          } />
          <Route path="/inbounds/delivery-receiving" element={
              <InboundProvider>
                <InboundSidebar>
                  <DeliveryReceiving />
                </InboundSidebar>
              </InboundProvider>
          } />
          {/* Standalone Delivery Receiving - Full screen for tablets */}
          <Route path="/delivery-receiving" element={<DeliveryReceiving standalone={true} />} />
          {/* PDA Scanner - Lightweight PWA for Android terminals */}
          <Route path="/pda-scanner" element={<PDAScanner />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/financials" element={<Financials />} />
          <Route path="/test-printer-dropdown" element={<TestPrinterDropdown />} />

          {/* Stallion Routes */}
          <Route path="/stallion-status" element={<StallionStatus />} />
          <Route path="/stallion-reconcile" element={<StallionReconcile />} />
          <Route path="/stallion-check" element={<StallionCheck />} />
          <Route path="/stallion" element={<Stallion />} />

          {/* Pattern Route */}
          <Route path="/pattern" element={<PatternAnalysis />} />

          {/* FBA Inbound v2024 Routes */}
          <Route path="/fba-inbound" element={
            <FBAInboundProvider>
              <FBAInboundSidebar>
                <FBAInboundWizard />
              </FBAInboundSidebar>
            </FBAInboundProvider>
          } />
          <Route path="/fba-inbound/:outboundId" element={
            <FBAInboundProvider>
              <FBAInboundSidebar>
                <FBAInboundWizard />
              </FBAInboundSidebar>
            </FBAInboundProvider>
          } />

          {/* Analytics Engine Routes - PIN protected, not in sidebar */}
          <Route path="/analytics/*" element={
            <AnalyticsProvider>
              <PinProtection>
                <AnalyticsLayout />
              </PinProtection>
            </AnalyticsProvider>
          } />
            </Routes>
          </BillingProvider>
      </Router>
      <Toaster />
    </TooltipProvider>
  );
}

export default App;
